import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { VueConstructor } from 'vue/types/umd'
import { mapGetters, mapMutations } from 'vuex'
import client from '@/api'
import api from '@orgmap/api'
import {
  TOrgContainerItemAddMutationPayload,
  TOrgContainerItemDelMutationPayload,
  TWishContainerSelectAllPayload,
  TWishContainerSelectPayload,
  TOrgContainerActivateMutationPayload,
} from '../store/mutations'
import { WishList } from '../components'
import * as types from '../store/types'
import { i18n } from '@appbase/util/i18n-setup'

const alertAsync = async (message: string) => {
  const alertHandler = (window as any).alertAsync

  if (typeof alertHandler === 'function') {
    await alertHandler(message)
    return
  }

  window.alert(message)
}

@Component({
  components: { WishList },
  methods: {
    ...mapMutations({
      registerContainer: types.ORG_CONTAINER_REGISTER,
      addToContainer: types.ORG_CONTAINER_ITEM_ADD,
      updateContainer: types.ORG_CONTAINER_UPDATE,
      deleteFromContainer: types.ORG_CONTAINER_ITEM_DEL,
      select: types.ORG_CONTAINER_ITEM_SELECT,
      selectAll: types.ORG_CONTAINER_ITEM_SELECT_ALL,
      activateContainer: types.ORG_CONTAINER_ACTIVATE,
    }),
  },
  computed: {
    ...mapGetters({
      wishContainers: types.ORG_CONTAINER_REGISTER,
      members: types.ORG_MEMBER_LIST,
      options: types.ORG_OPTION_GET,
    }),
  },
})
export default class WishListContainer extends Vue {
  showDeptSelect: boolean = false
  mDeptOption: 'child' | 'me' = 'me'

  //
  // ────────────────────────────────────────────────── I ──────────
  //   :::::: P R O P S : :  :   :    :     :        :          :
  // ────────────────────────────────────────────────────────────
  //

  @Prop({ type: Object }) item!: orgmap.TWishContainerOptionItem
  @Prop({ type: Object, default: null }) selectedDept!: orgmap.TDepartmentTreeItem | null

  //
  // ────────────────────────────────────────────────────────── I ──────────
  //   :::::: M U T A T I O N S : :  :   :    :     :        :          :
  // ────────────────────────────────────────────────────────────────────
  //

  registerContainer!: (item: orgmap.TWishContainerOptionItem) => void
  select!: (payload: TWishContainerSelectPayload) => void
  selectAll!: (payload: TWishContainerSelectAllPayload) => void
  addToContainer!: (payload: TOrgContainerItemAddMutationPayload) => void
  updateContainer!: (payload: orgmap.TWishContainerOptionItem) => void
  deleteFromContainer!: (payload: TOrgContainerItemDelMutationPayload) => void
  activateContainer!: (payload: TOrgContainerActivateMutationPayload) => void

  //
  // ────────────────────────────────────────────────────── I ──────────
  //   :::::: G E T T E R S : :  :   :    :     :        :          :
  // ────────────────────────────────────────────────────────────────
  //

  wishContainers!: orgmap.TWishContainer[]
  members!: orgmap.TMemberList
  options!: orgmap.TWishContainerOptions

  //
  // ──────────────────────────────────────────────────────── I ──────────
  //   :::::: C O M P U T E D : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────────────
  //

  get container() {
    const { id } = this.item
    return this.wishContainers.find((c) => c.id === id)
  }

  get containerData(): orgmap.TWishContainer {
    return this.container || {
      ...this.item,
      activated: false,
      items: [],
      selectedAll: false,
    }
  }

  //
  // ──────────────────────────────────────────────── I ──────────
  //   :::::: D A T A : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────
  //


  //
  // ────────────────────────────────────────────────────────── I ──────────
  //   :::::: L I F E C Y C L E : :  :   :    :     :        :          :
  // ────────────────────────────────────────────────────────────────────
  //

  created() {
    this.registerContainer(this.item)
  }

  //
  // ────────────────────────────────────────────────────── I ──────────
  //   :::::: M E T H O D S : :  :   :    :     :        :          :
  // ────────────────────────────────────────────────────────────────
  //

  async getDepartmentItems(includeChildDept: boolean): Promise<orgmap.TDepartmentTreeItem[]> {
    if (!this.selectedDept) {
      return []
    }

    if (!includeChildDept) {
      return [this.selectedDept]
    }

    const response = await client.post<orgmap.TDepartmentTreeItem[] | { items?: orgmap.TDepartmentTreeItem[] }>({
      url: '/workflow/form/api/getchilddeptline',
      data: {
        comCode: this.selectedDept.comCode,
        deptCode: this.selectedDept.deptCode,
        deptLineList: [{
          comCode: this.selectedDept.comCode,
          deptCode: this.selectedDept.deptCode,
        }],
      },
    })

    const departments = Array.isArray(response)
      ? response
      : response?.items || []

    if (!departments.length) {
      return [this.selectedDept]
    }

    return departments.map((department) => ({
      ...department,
      comCode: department.comCode || this.selectedDept!.comCode,
    }))
  }

  toWishItem(user: orgmap.UserItem): orgmap.TWishContainerItem {
    return {
      ...user,
      id: user.personCode || user.id,
      name: user.displayName,
      title: user.titleName || '',
      personCode: user.personCode,
      deptCode: user.deptCode,
      deptName: user.deptName,
      comCode: user.comCode,
      email: user.email,
      text: user.displayName,
      value: user.email,
      selected: false,
    }
  }

  async addCurrentDepartmentMembers(includeChildDept: boolean) {
    if (!this.selectedDept || !this.container) {
      await alertAsync(i18n.t('부서를_추가할_수_없습니다').toString())
      return
    }

    const departments = await this.getDepartmentItems(includeChildDept)
    const usersByPersonCode = new Map<string, orgmap.TWishContainerItem>()

    for (const department of departments) {
      if (!department.deptCode || !department.comCode) {
        continue
      }

      const result = await api.hr.getEmployeeList({
        deptCode: department.deptCode,
        comCode: department.comCode,
      })

      result.items.forEach((user) => {
        const key = user.personCode || user.email

        if (key && this.container && this.container.items.findIndex((item) => item.personCode === user.personCode || item.value === user.email) < 0) {
          usersByPersonCode.set(key, this.toWishItem(user))
        }
      })
    }

    if (usersByPersonCode.size === 0) {
      await alertAsync(i18n.t('해당_부서에_사용자가_없습니다').toString())
      return
    }

    this.updateContainer({
      ...this.container,
      items: [...this.container.items, ...Array.from(usersByPersonCode.values())],
    })
  }

  openDeptSelect() {
    this.mDeptOption = 'me'
    this.showDeptSelect = true
  }

  closeDeptSelect() {
    this.showDeptSelect = false
    this.mDeptOption = 'me'
  }

  async addDeptResult() {
    this.showDeptSelect = false
    await this.addCurrentDepartmentMembers(this.mDeptOption === 'child')
  }

  handleDeleteClick() {
    this.handleDelete()
  }

  handleSelectAllClick(event: Event) {
    const target = event.target as HTMLInputElement | null
    this.handleSelectAll(!!target?.checked)
  }

  async handleAdd() {
    console.log(`${this.item.name}` + i18n.t('컨테이너에_추가').toString());

    const hasSelectedMember = this.members.items.some((item) => item.selected)

    if (hasSelectedMember) {
      this.addToContainer({ containerId: this.item.id })
      return
    }

    if (!this.options.addableDept || !this.selectedDept) {
      await alertAsync(i18n.t('부서를_추가할_수_없습니다').toString())
      return
    }

    this.openDeptSelect()
  }

  handleDeleteOne(item: orgmap.TWishContainerItem) {
    this.handleSelectAll(false);
    this.handleSelect(item);
    this.deleteFromContainer({ containerId: this.item.id })
  }

  /**
   * @param item? Wish List Item 우측에 있는 X 버튼 클릭 시 전달되는 아이템 객체
   */
  handleDelete(item?: orgmap.TWishContainerItem) {
    console.log(`${this.item.name} 컨테이너에 제거`)
    this.deleteFromContainer({ containerId: this.item.id })
  }

  handleSelect(item: orgmap.TWishContainerItem) {
    this.select({
      id: this.item.id,
      item,
    })
  }

  handleSelectAll(select: boolean) {
    this.selectAll({
      id: this.item.id,
      select: select,
    })
  }

  handleActivate(){
    this.activateContainer({ containerId: this.item.id });
  }

}
